import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Checkbox, Drawer, List, Modal, Select, Spin, TimePicker, message } from "antd";
import dayjs from "dayjs";
import {
  addHealthPlanByTime,
  addHealthPlanDays,
  deleteHealthPlanDays,
  fetchHealthClientDayPlan,
  fetchHealthPlanDaySessions,
  fetchHealthPlanDays,
  fetchHealthPlanTimeHalls,
  fetchHealthPlanTimeSlots
} from "./health-clients-api";
import ClientAppointmentPlanDaysWeekCalendar from "./client-appointment-plan-days-week-calendar";
import { pick, toNumber } from "./health-clients-utils";

/** Можно планировать вручную: не «Не планировать». */
const canPlanManual = (vidPan) => {
  const v = toNumber(vidPan);
  return v != null && v !== 0;
};

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const isUnpaidBlocked = (row, canUnpaid) => {
  if (canUnpaid) return false;
  return toNumber(row?.isPlat) === 1 && (toNumber(row?.symaPlat) ?? 0) === 0;
};

const DRAWER_WIDTH_KEY = "planOnline.health.clients.planDaysDrawer.width";
const MIN_WIDTH = 900;
const MAX_WIDTH = 1100;
const DEFAULT_WIDTH = 640;

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const formatTime = (value) => {
  if (value == null || value === "") return "";
  // Firebird TIME may come as "1970-01-01T10:30:00" or "10:30:00"
  const s = String(value);
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
};

/** Минуты от полуночи; null если разобрать нельзя. */
const timeToMinutes = (value) => {
  if (value == null || value === "") return null;
  const m = String(value).match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
};

/** Пересечение [beg, end) с часом [hour:00, hour+1:00). */
const sessionIntersectsHour = (session, hour) => {
  if (hour == null) return true;
  const beg = timeToMinutes(session.timeBeg);
  if (beg == null) return false;
  let end = timeToMinutes(session.timeEnd);
  if (end == null || end <= beg) end = beg + 1;
  const hourBeg = hour * 60;
  const hourEnd = hourBeg + 60;
  return beg < hourEnd && end > hourBeg;
};

const toDateParam = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const formatDayLabel = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY, dd") : "";
};

const buildStayDayKeys = (dateBeg, dateEnd) => {
  const beg = dayjs(dateBeg).startOf("day");
  const end = dayjs(dateEnd).startOf("day");
  if (!beg.isValid() || !end.isValid() || end.isBefore(beg, "day")) return [];
  const keys = [];
  let cur = beg;
  while (!cur.isAfter(end, "day")) {
    keys.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "day");
  }
  return keys;
};

const normalizeInfo = (data) => ({
  idClient: toNumber(pick(data, "idClient", "IdClient")),
  idStr: toNumber(pick(data, "idStr", "IdStr")),
  idUsl: toNumber(pick(data, "idUsl", "IdUsl")),
  tName: pick(data, "tName", "TName") ?? "",
  cnt: toNumber(pick(data, "cnt", "Cnt")) ?? 0,
  cntPlan: toNumber(pick(data, "cntPlan", "CntPlan")) ?? 0,
  vidPan: toNumber(pick(data, "vidPan", "VidPan")) ?? 0,
  timeMax: toNumber(pick(data, "timeMax", "TimeMax")) ?? 0,
  dateBeg: pick(data, "dateBeg", "DateBeg"),
  dateEnd: pick(data, "dateEnd", "DateEnd"),
  days: Array.isArray(pick(data, "days", "Days"))
    ? pick(data, "days", "Days").map((d) => ({
        docDate: pick(d, "docDate", "DocDate"),
        qty: toNumber(pick(d, "qty", "Qty")) ?? 0,
        key: toDateParam(pick(d, "docDate", "DocDate"))
      }))
    : []
});

const normalizeTimeSlot = (row) => ({
  idZal: toNumber(pick(row, "idZal", "IdZal")),
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  timeBeg: pick(row, "timeBeg", "TimeBeg"),
  timeEnd: pick(row, "timeEnd", "TimeEnd"),
  zalName: pick(row, "zalName", "ZalName") ?? "",
  sotrName: "",
  cntMax: null,
  cntPlan: null,
  qtyAssigned: 0,
  isTimeBusy: false,
  isBlockedUsl: Boolean(pick(row, "isBlockedUsl", "IsBlockedUsl")),
  isTimeSlot: true
});

const minutesToHm = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const normalizeSession = (row) => ({
  idZal: toNumber(pick(row, "idZal", "IdZal")),
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  timeBeg: pick(row, "timeBeg", "TimeBeg"),
  timeEnd: pick(row, "timeEnd", "TimeEnd"),
  zalName: pick(row, "zalName", "ZalName") ?? "",
  sotrName: pick(row, "sotrName", "SotrName") ?? "",
  cntMax: toNumber(pick(row, "cntMax", "CntMax")),
  cntPlan: toNumber(pick(row, "cntPlan", "CntPlan")),
  qtyAssigned: toNumber(pick(row, "qtyAssigned", "QtyAssigned")) ?? 0,
  isTimeBusy: Boolean(pick(row, "isTimeBusy", "IsTimeBusy"))
});

const normalizeDayPlanItem = (row) => {
  const docDate = pick(row, "docDate", "DocDate");
  return {
    docDate,
    dayKey: toDateParam(docDate),
    idZal: toNumber(pick(row, "idZal", "IdZal")),
    idStr: toNumber(pick(row, "idStr", "IdStr")),
    idClientStrUsl: toNumber(pick(row, "idClientStrUsl", "IdClientStrUsl")),
    idUsl: toNumber(pick(row, "idUsl", "IdUsl")),
    timeBeg: pick(row, "timeBeg", "TimeBeg"),
    timeEnd: pick(row, "timeEnd", "TimeEnd"),
    tName: pick(row, "tName", "TName") ?? "",
    zalName: pick(row, "zalName", "ZalName") ?? "",
    sotrName: pick(row, "sotrName", "SotrName") ?? "",
    isDel: toNumber(pick(row, "isDel", "IsDel")) ?? 0
  };
};

/**
 * Планирование по дням (TFrmPlanPeriod) и по времени (planner VID_PAN=100).
 */
const ClientAppointmentPlanDaysDrawer = ({
  open,
  idClient,
  idStr,
  appointments = [],
  onClose,
  onChanged
}) => {
  const law = useSelector((state) => state.user.session?.law);
  const canUnpaid = getLawFlag(law, 99) === 1;
  const [size, setSize] = useState(loadWidth);
  const [activeIdStr, setActiveIdStr] = useState(idStr);
  const [info, setInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [dayPlan, setDayPlan] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [onlyFree, setOnlyFree] = useState(true);
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [hourFilter, setHourFilter] = useState(null);
  const [halls, setHalls] = useState([]);
  const [idZal, setIdZal] = useState(0);
  const [timeModal, setTimeModal] = useState(null);
  const [timeBegPick, setTimeBegPick] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingDayPlan, setLoadingDayPlan] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  /** Локальные cnt/cntPlan по idStr — обновляются из info после планирования. */
  const [statsByStr, setStatsByStr] = useState({});
  const infoReqRef = useRef(0);
  const sessReqRef = useRef(0);
  const dayPlanReqRef = useRef(0);
  const dayPlanScrollRef = useRef(null);
  const dayPlanSectionRefs = useRef({});
  const appointmentsRef = useRef(appointments);
  appointmentsRef.current = appointments;

  const loadDayPlan = useCallback(
    () => {
      if (!idClient) {
        setDayPlan([]);
        return Promise.resolve();
      }
      const requestId = ++dayPlanReqRef.current;
      setLoadingDayPlan(true);
      return fetchHealthClientDayPlan(idClient, { all: true })
        .then((data) => {
          if (requestId !== dayPlanReqRef.current) return;
          setDayPlan(Array.isArray(data) ? data.map(normalizeDayPlanItem) : []);
        })
        .catch((e) => {
          if (requestId !== dayPlanReqRef.current) return;
          setDayPlan([]);
          message.error(e.message || "Не удалось загрузить план клиента");
        })
        .finally(() => {
          if (requestId === dayPlanReqRef.current) setLoadingDayPlan(false);
        });
    },
    [idClient]
  );

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const qtyByDate = useMemo(() => {
    const map = new Map();
    (info?.days || []).forEach((d) => {
      if (d.key) map.set(d.key, d.qty || 0);
    });
    return map;
  }, [info?.days]);

  const sessionFree = (session) => {
    if (session.cntMax == null || session.cntPlan == null) return null;
    return Math.max(0, session.cntMax - session.cntPlan);
  };

  const progressCnt = info?.cnt ?? statsByStr[activeIdStr]?.cnt ?? 0;
  const progressCntPlan = info?.cntPlan ?? statsByStr[activeIdStr]?.cntPlan ?? 0;
  /** Нужное количество услуги уже набрано — новые планирования недоступны. */
  const isQuotaFull = progressCnt > 0 && progressCntPlan >= progressCnt;

  const isTimeMode = (info?.vidPan ?? 0) === 100;
  const timeMax = info?.timeMax ?? 0;

  const isFreeSession = (session) => {
    if (session.isTimeSlot) {
      if (session.isBlockedUsl) return false;
      const beg = timeToMinutes(session.timeBeg);
      const end = timeToMinutes(session.timeEnd);
      if (beg == null || end == null || timeMax <= 0) return false;
      return end - beg >= timeMax;
    }
    if (session.isTimeBusy) return false;
    const free = sessionFree(session);
    return free == null || free > 0;
  };

  /** Можно ещё запланировать клиента на этот сеанс / интервал. */
  const canPlanSession = (session) => {
    if (isQuotaFull) return false;
    if ((session.qtyAssigned || 0) > 0) return false;
    if (session.isTimeBusy) return false;
    if (session.isBlockedUsl) return false;
    if (session.isTimeSlot) {
      const beg = timeToMinutes(session.timeBeg);
      const end = timeToMinutes(session.timeEnd);
      if (beg == null || end == null || timeMax <= 0) return false;
      return end - beg >= timeMax;
    }
    const free = sessionFree(session);
    return free == null || free > 0;
  };

  const hourButtons = useMemo(() => {
    const freeByHour = new Map();
    let freeAll = 0;

    sessions.forEach((s) => {
      const free = isFreeSession(s);
      if (free) freeAll += 1;

      const beg = timeToMinutes(s.timeBeg);
      if (beg == null) return;
      let end = timeToMinutes(s.timeEnd);
      if (end == null || end <= beg) end = beg + 1;
      const fromHour = Math.floor(beg / 60);
      // час конца не включаем, если сеанс кончается ровно на границе
      const toHour = Math.floor((end - 1) / 60);
      for (let h = fromHour; h <= toHour; h += 1) {
        if (h < 0 || h > 23) continue;
        if (!freeByHour.has(h)) freeByHour.set(h, 0);
        if (free) freeByHour.set(h, freeByHour.get(h) + 1);
      }
    });

    const hours = Array.from(freeByHour.keys()).sort((a, b) => a - b);
    return {
      freeAll,
      hours: hours.map((hour) => ({ hour, freeCount: freeByHour.get(hour) || 0 }))
    };
  }, [sessions, timeMax]);

  const visibleSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (!sessionIntersectsHour(s, hourFilter)) return false;

      const assigned = (s.qtyAssigned || 0) > 0;
      if (onlyFree) {
        if (s.isTimeSlot) {
          if (!isFreeSession(s)) return false;
        } else {
          const free = sessionFree(s);
          if (!(assigned || free == null || free > 0)) return false;
        }
      }
      if (onlyAvailable) {
        // Запланированные + те, куда ещё можно запланировать клиента.
        if (!(assigned || canPlanSession(s))) return false;
      }
      return true;
    });
  }, [sessions, onlyFree, onlyAvailable, hourFilter, isQuotaFull, timeMax]);

  const loadInfo = useCallback(
    (keepDate) => {
      if (!idClient || activeIdStr == null) return Promise.resolve();
      const requestId = ++infoReqRef.current;
      setLoadingInfo(true);
      setError("");
      return fetchHealthPlanDays(idClient, activeIdStr)
        .then((data) => {
          if (requestId !== infoReqRef.current) return;
          const next = normalizeInfo(data);
          setInfo(next);
          setStatsByStr((prev) => ({
            ...prev,
            [next.idStr]: {
              tName: next.tName,
              cnt: next.cnt,
              cntPlan: next.cntPlan
            }
          }));
          const dates = next.days.map((d) => d.key).filter(Boolean);
          setSelectedDate((prev) => {
            if (keepDate && prev && dates.includes(prev)) return prev;
            return dates[0] || null;
          });
        })
        .catch((e) => {
          if (requestId !== infoReqRef.current) return;
          setError(e.message || "Не удалось загрузить планирование");
          setInfo(null);
          setSelectedDate(null);
        })
        .finally(() => {
          if (requestId === infoReqRef.current) setLoadingInfo(false);
        });
    },
    [idClient, activeIdStr]
  );

  useEffect(() => {
    if (!open) return;
    setActiveIdStr(idStr);
    setDirty(false);
    setSessions([]);
    setHalls([]);
    setIdZal(0);
    setTimeModal(null);
    setSelectedDate(null);
    const next = {};
    (appointmentsRef.current || []).forEach((a) => {
      if (a?.idStr == null) return;
      next[a.idStr] = {
        tName: a.tName || "",
        cnt: toNumber(a.cnt) ?? 0,
        cntPlan: toNumber(a.cntPlan) ?? 0
      };
    });
    setStatsByStr(next);
  }, [open, idClient, idStr]);

  useEffect(() => {
    if (!open || activeIdStr == null) return;
    setSessions([]);
    setHalls([]);
    setIdZal(0);
    setSelectedDate(null);
    loadInfo(false);
  }, [open, idClient, activeIdStr, loadInfo]);

  useEffect(() => {
    if (!open || !idClient || activeIdStr == null || !info || !isTimeMode) {
      if (!isTimeMode) setHalls([]);
      return;
    }
    const beg = toDateParam(info.dateBeg);
    const end = toDateParam(info.dateEnd);
    fetchHealthPlanTimeHalls(idClient, activeIdStr, { dateBeg: beg, dateEnd: end })
      .then((data) => {
        const rows = Array.isArray(data)
          ? data.map((h) => ({
              idZal: toNumber(pick(h, "idZal", "IdZal")),
              name: pick(h, "name", "Name") ?? ""
            })).filter((h) => h.idZal != null)
          : [];
        setHalls(rows);
        setIdZal((prev) => (prev && rows.some((h) => h.idZal === prev) ? prev : rows[0]?.idZal || 0));
      })
      .catch(() => {
        setHalls([]);
        setIdZal(0);
      });
  }, [open, idClient, activeIdStr, info, isTimeMode]);

  useEffect(() => {
    if (!open || !idClient || activeIdStr == null || !selectedDate || !info) {
      setSessions([]);
      return;
    }

    const requestId = ++sessReqRef.current;
    setLoadingSessions(true);
    setHourFilter(null);

    const request = isTimeMode
      ? fetchHealthPlanTimeSlots(idClient, activeIdStr, selectedDate, idZal || 0).then((data) =>
          Array.isArray(data) ? data.map(normalizeTimeSlot) : []
        )
      : fetchHealthPlanDaySessions(idClient, activeIdStr, selectedDate).then((data) =>
          Array.isArray(data) ? data.map(normalizeSession) : []
        );

    request
      .then((rows) => {
        if (requestId !== sessReqRef.current) return;
        setSessions(rows);
      })
      .catch((e) => {
        if (requestId !== sessReqRef.current) return;
        setSessions([]);
        message.error(e.message || (isTimeMode ? "Не удалось загрузить интервалы" : "Не удалось загрузить сеансы"));
      })
      .finally(() => {
        if (requestId === sessReqRef.current) setLoadingSessions(false);
      });
  }, [open, idClient, activeIdStr, selectedDate, info, isTimeMode, idZal]);

  const hasInfo = info != null;

  useEffect(() => {
    if (!open || !hasInfo) {
      if (!open) setDayPlan([]);
      return;
    }
    loadDayPlan();
  }, [open, idClient, hasInfo, loadDayPlan]);

  const dayPlanGroups = useMemo(() => {
    const byDay = new Map();
    dayPlan.forEach((item) => {
      if (!item.dayKey) return;
      if (!byDay.has(item.dayKey)) byDay.set(item.dayKey, []);
      byDay.get(item.dayKey).push(item);
    });

    const stayKeys = buildStayDayKeys(info?.dateBeg, info?.dateEnd);
    const staySet = new Set(stayKeys);
    // Записи вне периода пребывания — отдельными днями в конце списка, по дате.
    const keys = [...stayKeys, ...Array.from(byDay.keys()).filter((k) => !staySet.has(k))].sort();

    return keys.map((key) => ({
      key,
      label: formatDayLabel(key) + (staySet.has(key) ? "" : " · вне периода пребывания"),
      items: byDay.get(key) || [],
      outside: !staySet.has(key)
    }));
  }, [dayPlan, info?.dateBeg, info?.dateEnd]);

  useEffect(() => {
    if (!selectedDate || !dayPlanGroups.length) return;
    const el = dayPlanSectionRefs.current[selectedDate];
    if (!el) return;
    // После отрисовки групп — к выбранному дню.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [selectedDate, dayPlanGroups, loadingDayPlan]);

  const refreshAfterMutation = (nextInfo) => {
    setDirty(true);
    if (nextInfo) {
      const next = normalizeInfo(nextInfo);
      setInfo(next);
      setStatsByStr((prev) => ({
        ...prev,
        [next.idStr]: {
          tName: next.tName,
          cnt: next.cnt,
          cntPlan: next.cntPlan
        }
      }));
      loadDayPlan();
    } else {
      loadInfo(true);
      loadDayPlan();
    }
    if (selectedDate && idClient && activeIdStr != null) {
      const requestId = ++sessReqRef.current;
      setLoadingSessions(true);
      const timeMode = (nextInfo ? normalizeInfo(nextInfo).vidPan : info?.vidPan) === 100;
      const request = timeMode
        ? fetchHealthPlanTimeSlots(idClient, activeIdStr, selectedDate, idZal || 0).then((data) =>
            Array.isArray(data) ? data.map(normalizeTimeSlot) : []
          )
        : fetchHealthPlanDaySessions(idClient, activeIdStr, selectedDate).then((data) =>
            Array.isArray(data) ? data.map(normalizeSession) : []
          );
      request
        .then((rows) => {
          if (requestId !== sessReqRef.current) return;
          setSessions(rows);
        })
        .finally(() => {
          if (requestId === sessReqRef.current) setLoadingSessions(false);
        });
    }
  };

  const runMutateSession = (session, action, timeRange) => {
    if (mutating || !idClient || activeIdStr == null) return Promise.resolve();
    const current = (appointments || []).find((a) => a.idStr === activeIdStr);
    if (action !== "cancel" && isUnpaidBlocked(current, canUnpaid)) {
      message.error("Запрещено планировать неоплаченные услуги");
      return Promise.resolve();
    }
    setMutating(true);

    let request;
    if (action === "cancel") {
      request = deleteHealthPlanDays(idClient, activeIdStr, [
        { idZal: session.idZal, idStr: session.idStr }
      ]);
    } else if (session.isTimeSlot || isTimeMode) {
      const beg = timeRange?.timeBeg ?? formatTime(session.timeBeg);
      const end = timeRange?.timeEnd ?? minutesToHm(timeToMinutes(session.timeBeg) + timeMax);
      request = addHealthPlanByTime(idClient, activeIdStr, [
        { idZal: session.idZal, idStr: session.idStr, timeBeg: beg, timeEnd: end }
      ]);
    } else {
      request = addHealthPlanDays(idClient, activeIdStr, [
        { idZal: session.idZal, idStr: session.idStr }
      ]);
    }

    return request
      .then((data) => {
        message.success(action === "cancel" ? "Сеанс снят" : "Сеанс назначен");
        setTimeModal(null);
        refreshAfterMutation(data);
      })
      .catch((e) => {
        message.error(e.message || "Операция не выполнена");
        refreshAfterMutation(null);
        return Promise.reject(e);
      })
      .finally(() => setMutating(false));
  };

  const openTimePlanModal = (session) => {
    const begMin = timeToMinutes(session.timeBeg);
    if (begMin == null || timeMax <= 0) {
      message.error("Не правильно настроена длительность услуги");
      return;
    }
    setTimeBegPick(dayjs().startOf("day").add(begMin, "minute"));
    setTimeModal(session);
  };

  const confirmTimePlan = () => {
    if (!timeModal || !timeBegPick) return;
    const begMin = timeBegPick.hour() * 60 + timeBegPick.minute();
    const endMin = begMin + timeMax;
    const slotBeg = timeToMinutes(timeModal.timeBeg);
    const slotEnd = timeToMinutes(timeModal.timeEnd);
    if (slotBeg == null || slotEnd == null || begMin < slotBeg || endMin > slotEnd) {
      message.error("Выбранное время вне свободного интервала");
      return;
    }
    runMutateSession(timeModal, "plan", {
      timeBeg: minutesToHm(begMin),
      timeEnd: minutesToHm(endMin)
    });
  };

  const mutateSession = (session, action) => {
    if (mutating || !idClient || activeIdStr == null) return;

    if (action !== "cancel") {
      if (session.isTimeSlot || isTimeMode) {
        openTimePlanModal(session);
        return;
      }
      runMutateSession(session, action);
      return;
    }

    const timeLabel = `${formatTime(session.timeBeg)}${
      session.timeEnd ? `–${formatTime(session.timeEnd)}` : ""
    }`;
    Modal.confirm({
      title: "Отменить назначение на сеанс?",
      content: [timeLabel, session.zalName, session.sotrName].filter(Boolean).join(" · "),
      okText: "Отменить назначение",
      cancelText: "Нет",
      okButtonProps: { danger: true },
      onOk: () => runMutateSession(session, "cancel")
    });
  };

  const runDeletePlanItem = (item) => {
    if (mutating || !idClient || item?.idClientStrUsl == null || item.isDel) return Promise.resolve();
    const items = [{ idZal: item.idZal, idStr: item.idStr }];
    setMutating(true);
    return deleteHealthPlanDays(idClient, item.idClientStrUsl, items)
      .then((data) => {
        message.success("Запись удалена из плана");
        if (item.idClientStrUsl === activeIdStr) {
          refreshAfterMutation(data);
          return;
        }
        setDirty(true);
        if (data) {
          const next = normalizeInfo(data);
          if (next.idStr != null) {
            setStatsByStr((prev) => ({
              ...prev,
              [next.idStr]: {
                tName: next.tName,
                cnt: next.cnt,
                cntPlan: next.cntPlan
              }
            }));
          }
        }
        loadDayPlan();
        // Занятость сеансов / интервалов текущего назначения могла измениться.
        if (selectedDate && activeIdStr != null) {
          const request =
            (info?.vidPan ?? 0) === 100
              ? fetchHealthPlanTimeSlots(idClient, activeIdStr, selectedDate, idZal || 0).then((data) =>
                  Array.isArray(data) ? data.map(normalizeTimeSlot) : []
                )
              : fetchHealthPlanDaySessions(idClient, activeIdStr, selectedDate).then((data) =>
                  Array.isArray(data) ? data.map(normalizeSession) : []
                );
          request.then((rows) => setSessions(rows)).catch(() => {});
        }
      })
      .catch((e) => {
        message.error(e.message || "Не удалось удалить");
        loadDayPlan();
        return Promise.reject(e);
      })
      .finally(() => setMutating(false));
  };

  const deletePlanItem = (item) => {
    if (mutating || !idClient || item?.idClientStrUsl == null || item.isDel) return;

    const timeLabel = `${formatTime(item.timeBeg)}${item.timeEnd ? `–${formatTime(item.timeEnd)}` : ""}`;
    Modal.confirm({
      title: "Удалить запись из плана?",
      content: [timeLabel, item.tName || "Услуга", item.zalName].filter(Boolean).join(" · "),
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () => runDeletePlanItem(item)
    });
  };

  const handleClose = () => {
    if (dirty) onChanged?.();
    onClose?.();
  };

  const uslOptions = useMemo(() => {
    const list = (appointments || []).filter((a) => canPlanManual(a.vidPan));
    return list.map((a) => {
      const stats = statsByStr[a.idStr];
      const cnt = stats?.cnt ?? toNumber(a.cnt) ?? 0;
      const cntPlan = stats?.cntPlan ?? toNumber(a.cntPlan) ?? 0;
      const tName = stats?.tName || a.tName || "Услуга";
      const complete = cnt > 0 && cntPlan >= cnt;
      const byTime = toNumber(a.vidPan) === 100;
      const unpaid = isUnpaidBlocked(a, canUnpaid);
      return {
        value: a.idStr,
        tName,
        cnt,
        cntPlan,
        complete,
        byTime,
        unpaid,
        disabled: unpaid,
        label: tName
      };
    });
  }, [appointments, statsByStr, canUnpaid]);

  const progressPct =
    progressCnt > 0 ? Math.min(100, Math.round((progressCntPlan / progressCnt) * 100)) : 0;
  const progressTone =
    progressCntPlan <= 0 ? "empty" : progressCnt > 0 && progressCntPlan >= progressCnt ? "full" : "partial";

  const unpaidBlocked = useMemo(() => {
    const current = (appointments || []).find((a) => a.idStr === activeIdStr);
    return isUnpaidBlocked(current, canUnpaid);
  }, [appointments, activeIdStr, canUnpaid]);

  const drawerTitle = (
    <div className="client-appointment-plan-days__title">
      <Select
        className="client-appointment-plan-days__usl-select"
        value={activeIdStr}
        options={uslOptions}
        optionLabelProp="label"
        popupMatchSelectWidth={false}
        optionRender={(option) => {
          const data = option.data;
          return (
            <div
              className={`client-appointment-plan-days__usl-option${
                data.complete ? " client-appointment-plan-days__usl-option--complete" : ""
              }`}
            >
              <span className="client-appointment-plan-days__usl-option-name">{data.tName}</span>
              <span className="client-appointment-plan-days__usl-option-meta">
                Запланировано {data.cntPlan} из {data.cnt}
                {data.byTime ? " · по времени" : ""}
                {data.unpaid ? " · неоплачено" : ""}
              </span>
            </div>
          );
        }}
        onChange={(value) => {
          if (value === activeIdStr) return;
          setActiveIdStr(value);
        }}
      />
      <div
        className={`client-appointment-plan-days__progress client-appointment-plan-days__progress--${progressTone}`}
      >
        <div className="client-appointment-plan-days__progress-track">
          <div
            className="client-appointment-plan-days__progress-fill"
            style={{ width: `${progressTone === "empty" ? 100 : progressPct}%` }}
          />
        </div>
        <div className="client-appointment-plan-days__progress-text">
          Запланировано {progressCntPlan} из {progressCnt}
        </div>
      </div>
    </div>
  );

  return (
    <Drawer
      placement="right"
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={handleClose}
      destroyOnClose
      title={drawerTitle}
      rootClassName="client-appointment-plan-days"
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}
      {unpaidBlocked ? (
        <Alert
          type="warning"
          showIcon
          message="Запрещено планировать неоплаченные услуги"
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {loadingInfo && !info ? (
        <div className="client-appointment-plan-days__center">
          <Spin tip="Загрузка..." />
        </div>
      ) : (
        <div className="client-appointment-plan-days__body">
          <div className="client-appointment-plan-days__calendar">
            <ClientAppointmentPlanDaysWeekCalendar
              dateBeg={info?.dateBeg}
              dateEnd={info?.dateEnd}
              selectedDate={selectedDate}
              qtyByDate={qtyByDate}
              onSelect={setSelectedDate}
            />
          </div>

          <div className="client-appointment-plan-days__side-filters">
            {isTimeMode ? (
              <div className="client-appointment-plan-days__hall-filter">
                <span className="client-appointment-plan-days__hall-label">Кабинет</span>
                <Select
                  style={{ width: "100%" }}
                  value={idZal || undefined}
                  placeholder="Кабинет"
                  options={halls.map((h) => ({ value: h.idZal, label: h.name || `№${h.idZal}` }))}
                  onChange={(value) => setIdZal(value || 0)}
                  allowClear={false}
                />
                {timeMax > 0 ? (
                  <div className="client-appointment-plan-days__time-max">Длительность {timeMax} мин.</div>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                    message="Не задана длительность услуги (TIME_MAX)"
                  />
                )}
              </div>
            ) : null}

            <div className="client-appointment-plan-days__hour-filters">
              {hourButtons.hours.length ? (
                <Button
                  size="small"
                  type={hourFilter == null ? "primary" : "default"}
                  className="client-appointment-plan-days__hour-btn"
                  onClick={() => setHourFilter(null)}
                >
                  <span className="client-appointment-plan-days__hour-btn-inner">
                    <span className="client-appointment-plan-days__hour-btn-label">Все</span>
                    <span className="client-appointment-plan-days__hour-btn-count">{hourButtons.freeAll}</span>
                  </span>
                </Button>
              ) : null}
              {hourButtons.hours.map(({ hour, freeCount }) => {
                const pressed = hourFilter === hour;
                return (
                  <Button
                    key={hour}
                    size="small"
                    type={pressed ? "primary" : "default"}
                    className="client-appointment-plan-days__hour-btn"
                    onClick={() => setHourFilter((prev) => (prev === hour ? null : hour))}
                  >
                    <span className="client-appointment-plan-days__hour-btn-inner">
                      <span className="client-appointment-plan-days__hour-btn-label">
                        {`${hour}:00`}
                      </span>
                      <span className="client-appointment-plan-days__hour-btn-count">{freeCount}</span>
                    </span>
                  </Button>
                );
              })}
            </div>

            {!isTimeMode ? (
              <div className="client-appointment-plan-days__side-checkboxes">
                <Checkbox checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)}>
                  Свободные
                </Checkbox>
                <Checkbox checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)}>
                  Доступные
                </Checkbox>
              </div>
            ) : null}
          </div>

          <div className="client-appointment-plan-days__client-plan">
            <div className="client-appointment-plan-days__panel-caption">План клиента</div>
            {loadingDayPlan && !dayPlanGroups.length ? (
              <div className="client-appointment-plan-days__center">
                <Spin size="small" />
              </div>
            ) : (
              <div ref={dayPlanScrollRef} className="client-appointment-plan-days__client-plan-scroll">
                {dayPlanGroups.length ? (
                  dayPlanGroups.map((group) => (
                    <div
                      key={group.key}
                      ref={(el) => {
                        if (el) dayPlanSectionRefs.current[group.key] = el;
                        else delete dayPlanSectionRefs.current[group.key];
                      }}
                      className={`client-appointment-plan-days__plan-day${
                        group.key === selectedDate
                          ? " client-appointment-plan-days__plan-day--active"
                          : ""
                      }${group.outside ? " client-appointment-plan-days__plan-day--outside" : ""}`}
                      data-day={group.key}
                    >
                      <div className="client-appointment-plan-days__plan-day-head">{group.label}</div>
                      {group.items.length ? (
                        <ul className="client-appointment-plan-days__plan-day-list">
                          {group.items.map((item) => {
                            const current = item.idClientStrUsl === activeIdStr;
                            return (
                              <li
                                key={`${item.idZal}-${item.idStr}-${item.idClientStrUsl}`}
                                className={`client-appointment-plan-days__plan-item${
                                  current ? " client-appointment-plan-days__plan-item--current" : ""
                                }${item.isDel ? " client-appointment-plan-days__plan-item--cancelled" : ""}`}
                              >
                                <div className="client-appointment-plan-days__session-main">
                                  <div className="client-appointment-plan-days__session-time">
                                    {formatTime(item.timeBeg)}
                                    {item.timeEnd ? `–${formatTime(item.timeEnd)}` : ""}
                                    {item.isDel ? " · отменён" : ""}
                                  </div>
                                  <div className="client-appointment-plan-days__session-meta">
                                    {item.tName || "Услуга"}
                                    {item.zalName ? ` · ${item.zalName}` : ""}
                                    {item.sotrName ? ` · ${item.sotrName}` : ""}
                                  </div>
                                </div>
                                {!item.isDel ? (
                                  <span className="client-appointment-plan-days__plan-item-actions">
                                    <Button
                                      type="link"
                                      size="small"
                                      danger
                                      disabled={mutating}
                                      onClick={() => deletePlanItem(item)}
                                    >
                                      Удалить
                                    </Button>
                                  </span>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="client-appointment-plan-days__plan-day-empty">Нет записей</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="client-appointment-plan-days__plan-day-empty">Нет дней в периоде</div>
                )}
              </div>
            )}
          </div>

          <div className="client-appointment-plan-days__sessions">
            <div className="client-appointment-plan-days__panel-caption">
              {isTimeMode ? "Свободные интервалы" : "Сеансы"}
            </div>
            {loadingSessions ? (
              <div className="client-appointment-plan-days__center">
                <Spin size="small" />
              </div>
            ) : (
              <List
                size="small"
                bordered
                dataSource={visibleSessions}
                locale={{
                  emptyText: selectedDate
                    ? isTimeMode
                      ? "Нет свободных интервалов"
                      : "Нет сеансов"
                    : "Выберите день"
                }}
                renderItem={(session) => {
                  const assigned = session.qtyAssigned > 0;
                  const timeBusy = !assigned && session.isTimeBusy;
                  const blocked = Boolean(session.isBlockedUsl);
                  const free = session.isTimeSlot ? null : sessionFree(session);
                  return (
                    <List.Item
                      className={`client-appointment-plan-days__session${
                        assigned ? " client-appointment-plan-days__session--assigned" : ""
                      }${timeBusy || blocked ? " client-appointment-plan-days__session--busy" : ""}`}
                    >
                      <div className="client-appointment-plan-days__session-main">
                        <div className="client-appointment-plan-days__session-time-row">
                          <span
                            className={`client-appointment-plan-days__session-time${
                              timeBusy || blocked
                                ? " client-appointment-plan-days__session-time--busy"
                                : ""
                            }`}
                          >
                            {formatTime(session.timeBeg)}
                            {session.timeEnd ? `–${formatTime(session.timeEnd)}` : ""}
                            {assigned ? " · назначен" : ""}
                            {blocked ? " · услуга уже в этот день" : ""}
                          </span>
                          <span className="client-appointment-plan-days__session-actions">
                            {assigned ? (
                              <Button
                                type="link"
                                size="small"
                                danger
                                disabled={mutating}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  mutateSession(session, "cancel");
                                }}
                              >
                                Отменить
                              </Button>
                            ) : timeBusy ? (
                              <span className="client-appointment-plan-days__session-busy-label">
                                Время занято
                              </span>
                            ) : blocked ? (
                              <span className="client-appointment-plan-days__session-busy-label">
                                Недоступно
                              </span>
                            ) : canPlanSession(session) ? (
                              <Button
                                type="link"
                                size="small"
                                className="client-appointment-plan-days__session-plan"
                                disabled={mutating}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  mutateSession(session, "plan");
                                }}
                              >
                                Планировать
                              </Button>
                            ) : null}
                          </span>
                        </div>
                        <div className="client-appointment-plan-days__session-meta">
                          {session.zalName || "Кабинет"}
                          {session.sotrName ? ` · ${session.sotrName}` : ""}
                          {free != null ? ` · своб. ${free}` : ""}
                          {session.cntMax != null ? ` / ${session.cntMax}` : ""}
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </div>
      )}

      <Modal
        open={Boolean(timeModal)}
        title="Планирование по времени"
        okText="Назначить"
        cancelText="Отмена"
        confirmLoading={mutating}
        onCancel={() => setTimeModal(null)}
        onOk={confirmTimePlan}
        destroyOnClose
      >
        {timeModal ? (
          <div className="client-appointment-plan-days__time-modal">
            <div>
              Свободный период{" "}
              <b>
                {formatTime(timeModal.timeBeg)}–{formatTime(timeModal.timeEnd)}
              </b>
              {timeModal.zalName ? ` · ${timeModal.zalName}` : ""}
            </div>
            <div style={{ marginTop: 12 }}>
              <span style={{ marginRight: 8 }}>Начало</span>
              <TimePicker
                value={timeBegPick}
                format="HH:mm"
                minuteStep={5}
                allowClear={false}
                onChange={(value) => setTimeBegPick(value)}
              />
              {timeBegPick && timeMax > 0 ? (
                <span style={{ marginLeft: 12, color: "#595959" }}>
                  до{" "}
                  {minutesToHm(timeBegPick.hour() * 60 + timeBegPick.minute() + timeMax)}
                  {" "}({timeMax} мин.)
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>
    </Drawer>
  );
};

export default ClientAppointmentPlanDaysDrawer;
